import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import card from '../../assets/images/icons/card.svg';
import pix from '../../assets/images/icons/pix.svg';
import close from '../../assets/images/close.svg';
import Text from '../../assets/styles/Text';
import Spinner from '../../assets/styles/spinner';
import ReactPortal from '../ReactPortal';
import ModelService from '../../services/ModelService';
import PaymentService from '../../services/PaymentService';
import { useStore } from '../../stores';
import * as styles from './styles';

function formatCpf(cpf) {
  if (!cpf) return '';
  return cpf.replace(/\D/g, '');
}

function formatPrice(value) {
  return value.toFixed(2).replace('.', ',');
}

export default function CheckoutModal({ show, onClose, plan, isUpgrade }) {
  const navigate = useNavigate();
  const user = useStore((state) => state.user);

  const [billingType, setBillingType] = useState(plan?.signature ? 'CREDIT_CARD' : 'PIX');
  const [planOption, setPlanOption] = useState(1);
  const [quantity, setQuantity] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [modelInfo, setModelInfo] = useState(null);
  const [pixData, setPixData] = useState(null);

  const { id: itemId, signature, days1, total1, days2, total2, days3, total3 } = plan || {};
  const hasOptions = !isUpgrade && !signature && total1 != null;

  // Dados específicos de upgrade
  const upgradeDays = plan?.days;
  const upgradeTotal = plan?.total;
  const showQuantitySelector = isUpgrade && !signature;

  function handleClose() {
    setBillingType(plan?.signature ? 'CREDIT_CARD' : 'PIX');
    setPlanOption(1);
    setQuantity(1);
    setModelInfo(null);
    setPixData(null);
    onClose();
  }

  useEffect(() => {
    if (!show) return;

    if (!user) {
      toast.error('Faça login para continuar');
      onClose();
      navigate('/modellogin');
      return;
    }

    if (user.type?.toUpperCase() !== 'MODEL') {
      toast.error('Apenas modelos podem adquirir planos');
      onClose();
      return;
    }

    ModelService.findInfoModel(user.id)
      .then(setModelInfo)
      .catch(() => {
        toast.error('Erro ao carregar dados da conta');
        onClose();
      });
  }, [show, user, navigate, onClose]);

  async function handleSubmit(e) {
    e.preventDefault();

    const customerData = {
      name: modelInfo?.fantasyName || modelInfo?.name || '',
      email: user.email,
      cpfCnpj: formatCpf(modelInfo?.cpf),
      phone: modelInfo?.phone || '',
    };

    const returnUrl = `${window.location.origin}/modeldashboard?payment=success`;

    let body;
    let checkoutFn;

    if (isUpgrade) {
      body = { upgradeId: itemId, billingType, customerData, returnUrl };
      if (!signature) body.quantity = quantity;
      checkoutFn = () => PaymentService.upgradeCheckout(body);
    } else {
      body = { planId: itemId, billingType, customerData, returnUrl };
      if (signature) {
        body.days = 30;
      } else {
        body.planOption = planOption;
      }
      checkoutFn = () => PaymentService.checkout(body);
    }

    setIsLoading(true);
    try {
      const result = await checkoutFn();
      const { payment } = result;

      if (payment?.pixQrCode) {
        setPixData({
          qrCode: payment.pixQrCode,
          copyPaste: payment.pixCopyPaste,
        });
        return;
      }

      if (payment?.invoiceUrl) {
        onClose();
        navigate('/payment', { state: { invoiceUrl: payment.invoiceUrl } });
        return;
      }

      toast.success('Assinatura criada! Verifique seu e-mail para concluir o pagamento.');
      handleClose();
      navigate('/modeldashboard');
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        'Erro ao processar pagamento';
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  }

  if (!show) return null;

  function renderContent() {
    if (pixData) {
      return (
        <styles.PaymentInfo>
          <Text.H2>Pague via PIX</Text.H2>
          <Text.Span>Escaneie o QR Code ou copie o código abaixo</Text.Span>
          <styles.PixQrCode
            src={`data:image/png;base64,${pixData.qrCode}`}
            alt="QR Code PIX"
          />
          <styles.PixCopyPaste>
            <Text.Span>{pixData.copyPaste}</Text.Span>
          </styles.PixCopyPaste>
          <styles.CopyButton
            type="button"
            onClick={() => {
              navigator.clipboard.writeText(pixData.copyPaste);
              toast.success('Código copiado!');
            }}
          >
            <Text.H4>COPIAR CÓDIGO</Text.H4>
          </styles.CopyButton>
          <Text.Span>Após o pagamento, seu plano será ativado automaticamente.</Text.Span>
          <styles.SubmitButton type="button" onClick={handleClose}>
            <Text.H4>FECHAR</Text.H4>
          </styles.SubmitButton>
        </styles.PaymentInfo>
      );
    }

    if (!modelInfo) {
      return (
        <styles.PaymentInfo>
          <Spinner size={48} />
        </styles.PaymentInfo>
      );
    }

    return (
      <styles.Form onSubmit={handleSubmit}>
        <Text.H2>Finalizar compra</Text.H2>

        {hasOptions && (
          <styles.OptionGroup>
            {[
              { opt: 1, days: days1, total: total1 },
              { opt: 2, days: days2, total: total2 },
              { opt: 3, days: days3, total: total3 },
            ].map(({ opt, days: d, total }) => (
              <styles.Option
                key={opt}
                type="button"
                $active={planOption === opt}
                onClick={() => setPlanOption(opt)}
              >
                <Text.H4>{d} dias</Text.H4>
                <Text.Span>R$ {formatPrice(total)}</Text.Span>
              </styles.Option>
            ))}
          </styles.OptionGroup>
        )}

        {showQuantitySelector && (
          <styles.QuantitySelector>
            <button
              type="button"
              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              disabled={quantity <= 1}
            >
              −
            </button>

            <styles.QuantityInfo>
              <Text.H2>{quantity}x</Text.H2>
              <Text.Span>
                {quantity * upgradeDays} dias — R${' '}
                {formatPrice(quantity * upgradeTotal)}
              </Text.Span>
            </styles.QuantityInfo>

            <button
              type="button"
              onClick={() => setQuantity((q) => Math.min(12, q + 1))}
              disabled={quantity >= 12}
            >
              +
            </button>
          </styles.QuantitySelector>
        )}

        {signature && (
          <styles.PeriodInfo>
            <Text.Span>
              Período: <strong>Mensal (30 dias)</strong>
            </Text.Span>
          </styles.PeriodInfo>
        )}

        <styles.BillingGroup>
          {!signature && (
            <styles.BillingOption
              type="button"
              $active={billingType === 'PIX'}
              onClick={() => setBillingType('PIX')}
            >
              <img src={pix} alt="pix" />
              <Text.Span>PIX</Text.Span>
            </styles.BillingOption>
          )}

          <styles.BillingOption
            type="button"
            $active={billingType === 'CREDIT_CARD'}
            onClick={() => setBillingType('CREDIT_CARD')}
          >
            <img src={card} alt="cartão" />
            <Text.Span>Cartão</Text.Span>
          </styles.BillingOption>
        </styles.BillingGroup>

        <styles.SubmitButton type="submit" disabled={isLoading}>
          {isLoading ? <Spinner size={36} /> : <Text.H4>PAGAR</Text.H4>}
        </styles.SubmitButton>
      </styles.Form>
    );
  }

  return (
    <ReactPortal containerId="CheckoutModal">
      <styles.Modal>
        <styles.Group>
          <button type="button" className="close" onClick={handleClose}>
            <img src={close} alt="fechar" />
          </button>

          {renderContent()}
        </styles.Group>
      </styles.Modal>
    </ReactPortal>
  );
}

CheckoutModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  plan: PropTypes.shape().isRequired,
  isUpgrade: PropTypes.bool,
};

CheckoutModal.defaultProps = {
  isUpgrade: false,
};
